import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import hbLogo from "@/assets/hb-design-logo.png";

const fmtPrice = (v) => {
  const n = Number(v);
  return v === "" || v == null || Number.isNaN(n) ? "-" : `$${n.toFixed(2)}`;
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Supported print layouts. Keys stored in sessionStorage; each defines the grid
// dimensions on an A4 portrait page. 1x1 renders the image on the top half only,
// leaving the bottom half intentionally blank.
const LAYOUTS = {
  "1x1": { cols: 1, rows: 1, perPage: 1, gridClass: "grid-cols-1 grid-rows-2", topHalfOnly: true, label: "1 × 1 (large, top-half image)" },
  "1x2": { cols: 1, rows: 2, perPage: 2, gridClass: "grid-cols-1 grid-rows-2", label: "1 × 2" },
  "1x3": { cols: 1, rows: 3, perPage: 3, gridClass: "grid-cols-1 grid-rows-3", label: "1 × 3" },
  "2x2": { cols: 2, rows: 2, perPage: 4, gridClass: "grid-cols-2 grid-rows-2", label: "2 × 2" },
  "2x3": { cols: 2, rows: 3, perPage: 6, gridClass: "grid-cols-2 grid-rows-3", label: "2 × 3" },
};

export default function CartGridPrint() {
  const navigate = useNavigate();
  const { dismiss } = useToast();
  const [printed, setPrinted] = useState(false);
  const autoPrintTimer = useRef(null);

  useEffect(() => {
    dismiss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { items, coefficient, showGtSku, layout } = useMemo(() => {
    try {
      const data = JSON.parse(sessionStorage.getItem("cartGridPrintData") || "{}");
      return {
        items: data.items || [],
        coefficient: Number(data.coefficient) || 1,
        showGtSku: !!data.showGtSku,
        layout: LAYOUTS[data.layout] ? data.layout : "2x2",
      };
    } catch {
      return { items: [], coefficient: 1, showGtSku: false, layout: "2x2" };
    }
  }, []);

  const layoutConfig = LAYOUTS[layout];
  // 1x1 layout doubles as a customer-facing sample sheet - hide the inventory QTY column
  // so it reads like a product spec rather than a stock report.
  const hideQty = layoutConfig.topHalfOnly;

  useEffect(() => {
    if (!items.length || printed) return;
    autoPrintTimer.current = setTimeout(() => {
      setPrinted(true);
      window.print();
    }, 2500);
    return () => clearTimeout(autoPrintTimer.current);
  }, [items, printed]);

  const pages = chunk(items, layoutConfig.perPage);

  return (
    <div className="min-h-screen bg-white" data-testid="cart-grid-print-page">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 6mm; }
          .cart-grid-print-page-block { page-break-after: always; height: 284mm !important; width: auto !important; }
          .cart-grid-print-page-block:last-child { page-break-after: auto; }
        }
      `}</style>
      <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-3 print:hidden">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm border-zinc-300"
          onClick={() => navigate("/cart")}
          data-testid="cart-grid-print-back-button"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <span className="text-sm text-zinc-500">{items.length} product(s) · portrait, {layoutConfig.label}</span>
        <Button
          size="sm"
          className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          onClick={() => window.print()}
          data-testid="cart-grid-print-now-button"
        >
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-sm text-zinc-500" data-testid="cart-grid-print-empty-state">
          Cart is empty. Go back and add products first.
        </div>
      ) : (
        <div className="bg-zinc-100 py-4 print:bg-white print:py-0">
        {pages.map((pageItems, pageIdx) => (
          <div
            key={pageIdx}
            className="cart-grid-print-page-block mx-auto mb-4 flex h-[297mm] w-[210mm] flex-col border border-zinc-300 bg-white p-[8mm] shadow-md print:mx-0 print:mb-0 print:border-0 print:p-0 print:shadow-none"
          >
            <div className="mb-3 shrink-0">
              <img src={hbLogo} alt="HB Design" className="h-7 w-auto" />
            </div>
            <div className={`grid min-h-0 flex-1 ${layoutConfig.gridClass} gap-4 print:gap-3`}>
            {pageItems.map((item, cardIdx) => {
              const src = `${API}/cart/items/${item.id}/photo`;
              const variants = item.variants || [];
              // 1x1 layout: render the single item on the TOP half only, leaving
              // row 2 blank (per user request). We span the card across only the
              // first grid row explicitly.
              const cardExtraClass = layoutConfig.topHalfOnly ? "row-start-1" : "";
              return (
                <div
                  key={item.id}
                  className={`flex min-h-0 flex-col overflow-hidden rounded-sm border border-zinc-300 ${cardExtraClass}`}
                  style={{ pageBreakInside: "avoid" }}
                  data-testid={`cart-grid-print-card-${item.id}`}
                >
                  <div className="flex min-h-0 flex-1 items-center justify-center border-b border-zinc-200 bg-zinc-50">
                    <img
                      src={src}
                      alt={item.item_no}
                      className="h-full w-full object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                  <div className="flex shrink-0 flex-col gap-0.5 p-2">
                    <span className="text-sm font-bold text-zinc-900">{item.item_no || "-"}</span>
                    <div className="line-clamp-2 text-xs text-zinc-600">{item.description || "-"}</div>
                    {variants.length > 0 && (
                      <table className="mt-1 w-full border-t border-zinc-200 text-[11px]">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-[0.03em] text-zinc-400">
                            <th className="pt-1 text-left font-normal">Size</th>
                            {showGtSku && <th className="pt-1 text-left font-normal">GT SKU</th>}
                            <th className="pt-1 text-right font-normal">Pcs/Ctn</th>
                            {!hideQty && <th className="pt-1 text-right font-normal">QTY</th>}
                            <th className="pt-1 text-right font-normal">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {variants.map((v, idx) => (
                            <tr key={idx} className="border-t border-zinc-100 text-zinc-700">
                              <td className="py-0.5">{(v.size || "").trim() || "-"}</td>
                              {showGtSku && <td className="py-0.5 font-bold">{v.gt_sku || "-"}</td>}
                              <td className="py-0.5 text-right">{v.packing_qty != null ? Math.round(v.packing_qty) : "-"}</td>
                              {!hideQty && (
                                <td className="py-0.5 text-right font-bold">{v.inventory_qty != null ? Math.round(v.inventory_qty) : "-"}</td>
                              )}
                              <td className="py-0.5 text-right font-bold">
                                {fmtPrice(v.final_price != null ? v.final_price : (Number(v.price) || 0) * coefficient)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
            <div className="mt-2 flex shrink-0 items-baseline gap-10">
              <span className="text-sm font-bold text-zinc-900">Better Home Fashion Inc</span>
              <span className="text-xs text-zinc-700">280 Bowes Road, Concord, Ontario L4K 1J9</span>
            </div>
          </div>
        ))}
        </div>
      )}
    </div>
  );
}
